import React, { useState, useCallback } from 'react';
import { Check } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import {
  FileText,
  CheckCircle,
  PaperPlane,
  ClipboardText,
  Scales,
  Briefcase,
} from '@phosphor-icons/react';

const iconMap = {
  FileText: FileText,
  CheckCircle: CheckCircle,
  PaperPlane: PaperPlane,
  ClipboardText: ClipboardText,
  Scales: Scales,
  Briefcase: Briefcase,
};

const TAB_SELF = 'self';
const TAB_ATTORNEY = 'attorney';

const tabConfig = {
  [TAB_SELF]: {
    label: 'Self Filing with Assistance',
    title: 'Self-Filing with Expert Assistance',
    subtitle: 'Complete Support Plan for Easy Filing',
    tmSignUpType: 1,
  },
  [TAB_ATTORNEY]: {
    label: 'IP Attorney-Led Filing',
    title: 'Attorney-Led Filing',
    subtitle: 'Complete End-to-End Service Plan',
    tmSignUpType: 2,
  },
};

const IntroSection = () => (
  <div className="text-center mb-12 max-w-4xl mx-auto">
    <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-4">
      Filing Options That Fit Your Needs
    </h2>
    <p className="text-base text-slate-600 leading-relaxed">
      From trademark registrations to ongoing monitoring, renewals, and more, our services at Trademark Throne are designed to secure your brand. Whether you need to register a business name, trademark a logo, or file a trademark for a slogan, we are here to assist. Our services cover all 50 U.S. states and extend globally, ensuring your intellectual property remains protected no matter where you are.
    </p>
  </div>
);

const TabNavigation = ({ activeTab, onTabChange }) => {
  const handleKeyDown = useCallback(
    (e, tabKey) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onTabChange(tabKey);
      }
    },
    [onTabChange]
  );

  return (
    <div className="flex justify-center mb-10" role="tablist" aria-label="Filing type selection">
      <div className="grid xs:inline-flex bg-slate-100 rounded-lg p-1 w-full xs:w-auto">
        {Object.entries(tabConfig).map(([key, config]) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${key}`}
              id={`tab-${key}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onTabChange(key)}
              onKeyDown={(e) => handleKeyDown(e, key)}
              className={`relative px-5 sm:px-8 py-3 text-sm font-semibold rounded-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#044B3B] focus:ring-offset-2 w-full xs:w-auto ${
                isActive
                  ? 'bg-[#044B3B] text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {config.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ProcessCard = ({ card }) => {
  const Icon = iconMap[card.icon] || FileText;
  return (
    <div className="bg-white border border-slate-200 hover:border-[#044B3B]/30 hover:shadow-lg transition-all duration-300 rounded-lg p-6 text-center h-full flex flex-col">
      <div className="w-12 h-12 bg-[#044B3B]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
        <Icon className="h-6 w-6 text-[#044B3B]" weight="regular" />
      </div>
      <h3 className="text-base font-heading font-semibold text-slate-900 mb-2">
        {card.title}
      </h3>
      <p className="text-sm text-slate-500 leading-relaxed flex-1">
        {card.description}
      </p>
    </div>
  );
};

const ProcessCards = ({ cards }) => {
  const [emblaRef] = useEmblaCarousel(
    { loop: true, align: 'start', duration: 40 },
    [Autoplay({ delay: 3500, stopOnInteraction: false })]
  );

  return (
    <>
      {/* Grid for sm+ screens */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-14 max-xs:hidden">
        {cards.map((card) => (
          <ProcessCard key={card.id} card={card} />
        ))}
      </div>

      {/* Slider for mobile (576px and below) */}
      <div className="overflow-hidden xs:hidden mb-14" ref={emblaRef}>
        <div className="flex gap-4">
          {cards.map((card) => (
            <div key={card.id} className="flex-[0_0_85%] min-w-0 h-full">
              <ProcessCard card={card} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const PricingSectionHeader = ({ title, subtitle }) => (
  <div className="text-center mb-10">
    <h3 className="text-2xl sm:text-3xl font-heading font-medium text-slate-900 tracking-tight mb-2">
      {title}
    </h3>
    <p className="text-sm text-slate-500">{subtitle}</p>
  </div>
);

const PricingCardV2 = ({ plan, onOpenModal, tmSignUpType, tabLabel }) => {
  const handleClick = () => {
    if (onOpenModal) {
      onOpenModal({
        context: `${tabLabel} - ${plan.name} Plan`,
        packageTitle: plan.name,
        packageAmount: plan.price,
        tmSignUpType: tmSignUpType,
      });
    }
  };

  return (
    <Card
      className={`relative overflow-hidden bg-white rounded-md h-full flex flex-col ${
        plan.popular
          ? 'border-2 border-[#044B3B] shadow-[0_0_24px_rgba(4,75,59,0.15)]'
          : 'border border-slate-200 hover:border-[#044B3B]/30 hover:shadow-lg'
      } transition-all duration-300`}
    >
      {plan.popular && (
        <div className="absolute top-0 left-0 right-0 bg-[#044B3B] text-white text-xs font-bold text-center py-1.5 uppercase tracking-wider">
          Most Popular
        </div>
      )}
      <div className={`p-6 flex flex-col flex-1 ${plan.popular ? 'pt-10' : ''}`}>
        <h3 className="text-lg font-heading font-bold text-slate-900 mb-3">
          {plan.name}
        </h3>
        <div className="mb-4">
          <span className="text-4xl font-heading font-semibold text-slate-900">
            ${plan.price}
          </span>
          <span className="text-slate-500 text-sm ml-1 block mt-2">
            {plan.govtFeeIncluded ? 'Govt. filing fee included' : 'Govt. filing fee not included'}
          </span>
        </div>

        {/* Payment Notice */}
        {plan.paymentNotice && (
          <div className="bg-slate-50 border border-slate-100 rounded-md p-3 mb-4">
            <p className="text-xs text-slate-500">{plan.paymentNotice}</p>
          </div>
        )}

        <ul className="space-y-3 mb-6 flex-1 overflow-y-auto pr-2 scrollbar-thin max-h-[300px]">
          {plan.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <Check className="h-4 w-4 text-[#044B3B] flex-shrink-0 mt-0.5" />
              <span className="text-sm text-slate-600">{feature}</span>
            </li>
          ))}
        </ul>
        <Button
          onClick={handleClick}
          className={`w-full font-semibold text-sm rounded-md ${
            plan.popular
              ? 'bg-[#044B3B] hover:bg-[#03392D] text-white'
              : 'bg-slate-900 hover:bg-slate-800 text-white'
          }`}
        >
          Get Started
        </Button>
        <p className="text-xs text-slate-400 text-center mt-3 hidden">
          *(Govt. filing fee not included)
        </p>
      </div>
    </Card>
  );
};

const PricingCardsGrid = ({ plans, onOpenModal, tmSignUpType, tabLabel }) => (
  <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-5">
    {plans.map((plan) => (
      <PricingCardV2
        key={plan.id}
        plan={plan}
        onOpenModal={onOpenModal}
        tmSignUpType={tmSignUpType}
        tabLabel={tabLabel}
      />
    ))}
  </div>
);

const TabContent = ({ activeTab, onOpenModal, selfFilingProcessCards, attorneyLedProcessCards, selfFilingPlans, attorneyLedPlans }) => {
  const currentConfig = tabConfig[activeTab];
  const currentProcessCards = activeTab === TAB_SELF ? selfFilingProcessCards : attorneyLedProcessCards;
  const currentPlans = activeTab === TAB_SELF ? selfFilingPlans : attorneyLedPlans;

  return (
    <div
      id={`panel-${activeTab}`}
      role="tabpanel"
      aria-labelledby={`tab-${activeTab}`}
      className="animate-fadeIn"
    >
      {/* Process Cards */}
      <ProcessCards cards={currentProcessCards} />

      {/* Pricing Section Header */}
      <PricingSectionHeader
        title={currentConfig.title}
        subtitle={currentConfig.subtitle}
      />

      {/* Pricing Cards */}
      <PricingCardsGrid
        plans={currentPlans}
        onOpenModal={onOpenModal}
        tmSignUpType={currentConfig.tmSignUpType}
        tabLabel={currentConfig.label}
      />
    </div>
  );
};

const PricingSectionV2 = ({
  onOpenModal,
  selfFilingProcessCards = [],
  attorneyLedProcessCards = [],
  selfFilingPlans = [],
  attorneyLedPlans = [],
}) => {
  const [activeTab, setActiveTab] = useState(TAB_SELF);
  const [visibleTab, setVisibleTab] = useState(TAB_SELF);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleTabChange = useCallback(
    (tabKey) => {
      if (tabKey === activeTab || isTransitioning) return;
      setIsTransitioning(true);
      // DEBUG: Log tab change and corresponding tmSignUpType
      const tmType = tabConfig[tabKey].tmSignUpType;
      console.log(
        `[DEBUG] Tab switched to: ${tabConfig[tabKey].label} -> tmSignUpType: ${tmType}`
      );
      // Small delay for exit animation
      setTimeout(() => {
        setActiveTab(tabKey);
        setVisibleTab(tabKey);
        setIsTransitioning(false);
      }, 150);
    },
    [activeTab, isTransitioning]
  );

  return (
    <section data-testid="pricing-section-v2" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro Section */}
        <IntroSection />

        {/* Tab Navigation */}
        <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} />

        {/* Tab Content Panel with smooth transition */}
        <div
          className={`transition-all duration-300 ease-in-out ${
            isTransitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
          }`}
        >
          <TabContent
            activeTab={visibleTab}
            onOpenModal={onOpenModal}
            selfFilingProcessCards={selfFilingProcessCards}
            attorneyLedProcessCards={attorneyLedProcessCards}
            selfFilingPlans={selfFilingPlans}
            attorneyLedPlans={attorneyLedPlans}
          />
        </div>
      </div>
    </section>
  );
};

export default PricingSectionV2;
